import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import ConsultOrdersView from '@/views/reports/ConsultOrdersView.vue'
import AdminLayout from '@/layouts/AdminLayout.vue'
import ManageBotsView from '@/views/bots/ManageBotsView.vue'
import ManageLinksView from '@/views/links/ManageLinksView.vue'
import ManagePaymentPlansView from '@/views/payment-plans/ManagePaymentPlansView.vue'
import ManageGroupsView from '@/views/groups/ManageGroupsView.vue'
import ManageMessagesView from '@/views/messages/ManageMessagesView.vue'
import ManageLeadsView from '@/views/leads/ManageLeadsView.vue'
import ManagePackagesView from '@/views/packages/ManagePackagesView.vue'
import ManageAffiliationView from '@/views/affiliation/ManageAffiliationView.vue'
import HelpView from '@/views/help/HelpView.vue'
import LoginExpiredView from '@/views/auth/LoginExpired.vue'
import MyAccountView from '@/views/account/MyAccountView.vue'
import LoginView from '@/views/auth/LoginView.vue'
import RegisterView from '@/views/auth/RegisterView.vue'
import OrganizationView from '@/views/auth/OrganizationView.vue'
import TwoFactorAuthView from '@/views/auth/TwoFactorAuthView.vue'
import Cookies from 'js-cookie'
import ChatsView from '@/views/chats/ChatsView.vue'
import IntegrationView from '@/views/integrations/IntegrationView.vue'
// import AwardsView from '@/views/awards/AwardsView.vue'
import PasswordRecoveryView from '@/views/auth/PasswordRecoveryView.vue'
import ResetPasswordView from '@/views/auth/ResetPasswordView.vue'
import LeadStartParametersView from '@/views/leadStartParameters/LeadStartParametersView.vue'
import ManageFlowsView from '@/views/chatFlow/ManageFlowsView.vue'
import FlowView from '@/views/chatFlow/FlowView.vue'
import ManageUtmLinks from '@/views/utmLinks/ManageUtmLinks.vue'
import AwardsView from '@/views/profile/AwardsView.vue'
import ProfileView from '@/views/profile/ProfileView.vue'
import ManageOrderBumpsView from '@/views/order-bumps/ManageOrderBumpsView.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login-expired',
    name: 'login-expired',
    component: LoginExpiredView
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { guest: true }
  },
  {
    path: '/2fa',
    name: '2fa',
    component: TwoFactorAuthView,
    meta: { requires2FA: true }
  },
  {
    path: '/password-recovery',
    name: 'password-recovery',
    component: PasswordRecoveryView
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: ResetPasswordView,
    props: true
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView
  },
  {
    path: '/video-call/:botUuid/:userId/:videoCallId',
    alias: '/video-calls/:botUuid/:userId/:videoCallId',
    name: 'video-call',
    component: () => import('../views/VideoCallView.vue')
  },
  {
    path: '/organizations',
    name: 'organizations',
    component: OrganizationView,
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    component: AdminLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '/',
        name: 'home',
        component: HomeView
      },
      {
        path: '/bots/manage',
        name: 'bots.manage',
        component: ManageBotsView
      },
      {
        path: '/reports/consult-orders',
        name: 'reports.consult-orders',
        component: ConsultOrdersView
      },
      {
        path: '/reports/dashboard',
        name: 'reports.dashboard',
        component: () => import('@/views/reports/ReportsDashboardView.vue')
      },
      {
        path: '/links/manage',
        name: 'links.manage',
        component: ManageLinksView
      },
      {
        path: '/payment-plans/manage',
        name: 'payment-plans.manage',
        component: ManagePaymentPlansView
      },
      {
        path: '/order-bumps/manage',
        name: 'order-bumps.manage',
        component: ManageOrderBumpsView
      },
      {
        path: '/groups/manage',
        name: 'groups.manage',
        component: ManageGroupsView
      },
      {
        path: '/messages',
        name: 'messages.manage',
        component: ManageMessagesView
      },
      {
        path: '/leads',
        name: 'leads.manage',
        component: ManageLeadsView
      },
      {
        path: '/packages',
        name: 'packages.manage',
        component: ManagePackagesView
      },
      {
        path: '/affiliation',
        name: 'affiliation.manage',
        component: ManageAffiliationView
      },
      {
        path: '/help',
        name: 'help',
        component: HelpView
      },
      {
        path: '/my-account',
        name: 'my-account',
        component: MyAccountView
      },
      {
        path: '/chats',
        name: 'chats',
        component: ChatsView
      },
      {
        path: '/integrations',
        name: 'integrations',
        component: IntegrationView
      },
      {
        path: '/leads/start-parameters',
        name: 'leads.start-parameters',
        component: LeadStartParametersView
      },
      {
        path: '/flows/manage',
        name: 'flows.manage',
        component: ManageFlowsView
      },
      {
        path: '/flows/:flowId',
        name: 'flows.show',
        component: FlowView
      },
      {
        path: '/utm-links/manage',
        name: 'utm-links.manage',
        component: ManageUtmLinks
      },
      {
        path: '/video-calls/manage',
        name: 'video-calls.manage',
        component: () => import('../views/videoCalls/ManageVideoCallsView.vue')
      },
      {
        path: '/awards',
        name: 'awards',
        component: AwardsView
      },
      {
        path: '/profile',
        name: 'profile',
        component: ProfileView
      },
      {
        path: '/payment-gateway-integrations/manage',
        name: 'payment-gateway-integrations.manage',
        component: () => import('../views/paymentGatewayIntegrations/ManagePaymentGatewayIntegrationsView.vue')
      },
    ],
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to, from, next) => {
  const authRoutes = ['login', 'register', 'password-recovery', 'reset-password']
  const token = Cookies.get('token')
  const organization = Cookies.get('organization')
  const is2AF = Cookies.get('is2AF') === 'true'
  const valid2AF = Cookies.get('valid2AF') === 'true'

  // If trying to access 2FA page
  if (to.meta.requires2FA) {
    if (!token) {
      // No token, redirect to login
      next({ name: 'login' })
    } else if (!is2AF) {
      // Token exists but 2FA not required, go to organizations
      next({ name: 'organizations' })
    } else {
      // Token exists and 2FA required
      next()
    }
    return
  }

  // If trying to access guest routes (login, register)
  if (to.meta.guest) {
    if (token && !is2AF && organization) {
      // Already logged in with organization
      next({ name: 'home' })
    } else if (token && !is2AF && !organization) {
      // Logged in but no organization
      next({ name: 'organizations' })
    } else if (token && is2AF && !valid2AF) {
      // Logged in but needs 2FA
      next({ name: '2fa' })
    } else {
      // Not logged in
      next()
    }
    return
  }

  // If trying to access protected routes
  if (to.meta.requiresAuth) {
    if (!token) {
      // No token, redirect to login
      next({ name: 'login' })
    } else if (is2AF && !valid2AF) {
      // Has token but needs 2FA validation
      next({ name: '2fa' })
    } else if (to.name !== 'organizations' && !organization) {
      // Has token but no organization selected
      next({ name: 'organizations' })
    } else {
      // Authenticated and validated
      next()
    }
    return
  }

  // Production environment check (legacy)
  if (process.env.ENVIRONMENT === 'production') {
    if (!authRoutes.includes(to.name as string) && !token) {
      next({ name: 'login' })
      return
    }
  }

  next()
})

export default router
